import { useMutation, useQueryClient } from "@tanstack/react-query";
import { locationAuditService } from "./location-audit.services";
import { LOCATION_AUDIT_KEYS } from "./keys";
import {
  CreateLocationAuditDto,
  UpdateLocationAuditDto,
  CompleteLocationAuditDto,
  AddAuditItemDto,
  BulkAddAuditItemsDto,
  UpdateAuditItemDto,
} from "~/types/location-audit";

export const useCreateLocationAudit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateLocationAuditDto) =>
      locationAuditService.createAudit(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LOCATION_AUDIT_KEYS.all });
    },
  });
};

export const useUpdateLocationAudit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateLocationAuditDto }) =>
      locationAuditService.updateAudit(id, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: LOCATION_AUDIT_KEYS.GET_AUDIT(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: LOCATION_AUDIT_KEYS.all });
    },
  });
};

export const useCompleteLocationAudit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto?: CompleteLocationAuditDto;
    }) => locationAuditService.completeAudit(id, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: LOCATION_AUDIT_KEYS.GET_AUDIT(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: LOCATION_AUDIT_KEYS.all });
    },
  });
};

export const useAddAuditItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, dto }: { auditId: string; dto: AddAuditItemDto }) =>
      locationAuditService.addItem(auditId, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: LOCATION_AUDIT_KEYS.GET_AUDIT(variables.auditId),
      });
    },
  });
};

export const useBulkAddAuditItems = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      auditId,
      dto,
    }: {
      auditId: string;
      dto: BulkAddAuditItemsDto;
    }) => locationAuditService.bulkAddItems(auditId, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: LOCATION_AUDIT_KEYS.GET_AUDIT(variables.auditId),
      });
    },
  });
};

export const useUpdateAuditItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      auditId,
      itemId,
      dto,
    }: {
      auditId: string;
      itemId: string;
      dto: UpdateAuditItemDto;
    }) => locationAuditService.updateItem(auditId, itemId, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: LOCATION_AUDIT_KEYS.GET_AUDIT(variables.auditId),
      });
    },
  });
};

export const useRemoveAuditItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, itemId }: { auditId: string; itemId: string }) =>
      locationAuditService.removeItem(auditId, itemId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: LOCATION_AUDIT_KEYS.GET_AUDIT(variables.auditId),
      });
    },
  });
};
